import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Eraser, Trash2, Loader2, Palette } from "lucide-react";
import api from "@/lib/api";
import { useZoomPan, canvasPos, ZoomControls } from "@/components/drawing/ZoomPan";
import { useRuler, RulerButton, DimensionDialog, MeasureHint } from "@/components/drawing/Ruler";
import { WhatsNewTour } from "@/components/whatsnew/WhatsNewTour";
import { VCheckTransferBar } from "@/components/drawing/VCheckTransferBar";

const COLORS = ["#FFFFFF", "#00E575", "#F59E0B", "#EF4444", "#3B82F6", "#A78BFA"];
const SIZES = [2, 4, 6, 10];

const SKETCH_CATEGORIES = ["Grundriss","Heizraum","Leitungsweg","Außeneinheit","Hydraulik grob","Transportweg","Sonstiges"];

export default function SketchCanvas() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnTab = params.get("return");
  const backUrl = `/projects/${id}${returnTab ? `?tab=${returnTab}` : ""}`;
  const canvasRef = useRef(null);
  const zp = useZoomPan();
  const ctxRef = useRef(null);
  const drawing = useRef(false);
  const lastPoint = useRef(null);
  const [color, setColor] = useState("#00E575");
  const [size, setSize] = useState(4);
  const [isEraser, setIsEraser] = useState(false);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(() => (SKETCH_CATEGORIES.includes(params.get("category")) ? params.get("category") : "Heizraum"));
  const [saving, setSaving] = useState(false);
  const ruler = useRuler({ ctxRef, canvasRef, color: isEraser ? "#FFFFFF" : color, zoom: zp.zoom });

  useEffect(() => {
    const canvas = canvasRef.current;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.parentElement.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    // Feste CSS-Größe, damit Vollbild/Zoom die Zeichnung nicht verzerrt
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.fillStyle = "#18181B";
    ctx.fillRect(0, 0, rect.width, rect.height);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctxRef.current = ctx;
    // Gemerkte Lineal-Referenz der letzten Skizze übernehmen
    api.get(`/projects/${id}/measure-ref`).then((r) => {
      if (r.data?.ref_mm_per_width) {
        ruler.loadRef(r.data.ref_mm_per_width);
        toast.info("Lineal-Referenz der letzten Skizze übernommen – über „Referenz zurücksetzen“ änderbar.");
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPos = (e) => canvasPos(canvasRef.current, e);

  const start = (e) => {
    if (zp.gestureStart(e)) { drawing.current = false; if (ruler.dragging()) ruler.cancel(); return; }
    e.preventDefault();
    if (ruler.active) { ruler.down(getPos(e), e); return; }
    drawing.current = true;
    lastPoint.current = getPos(e);
  };
  const move = (e) => {
    if (zp.gestureMove(e)) return;
    if (ruler.dragging()) { e.preventDefault(); ruler.update(getPos(e)); return; }
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = ctxRef.current;
    const p = getPos(e);
    ctx.strokeStyle = isEraser ? "#18181B" : color;
    ctx.lineWidth = (isEraser ? size * 3 : size) / zp.zoom;
    ctx.beginPath();
    ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPoint.current = p;
  };
  const end = (e) => { zp.gestureEnd(e); if (ruler.dragging() && !ruler.pending) ruler.finish(); drawing.current = false; };

  const clear = () => {
    ruler.trimTo(0);
    const ctx = ctxRef.current;
    const c = canvasRef.current;
    ctx.fillStyle = "#18181B";
    ctx.fillRect(0, 0, c.width, c.height);
  };

  const save = async () => {
    setSaving(true);
    try {
      const blob = await new Promise((res) => canvasRef.current.toBlob(res, "image/png"));
      const fd = new FormData();
      fd.append("file", blob, `sketch-${Date.now()}.png`);
      fd.append("category", category);
      fd.append("title", title || `Skizze ${category}`);
      fd.append("measurements", JSON.stringify(ruler.measurements));
      if (ruler.refPerWidth()) fd.append("ref_mm_per_width", String(ruler.refPerWidth()));
      await api.post(`/projects/${id}/sketches`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Skizze gespeichert");
      navigate(backUrl);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`bg-[#09090B] flex flex-col ${zp.fullscreen ? "fixed inset-0 z-[80]" : "min-h-screen"}`} data-testid="sketch-screen">
      <header className={`bg-[#09090B]/95 border-b border-zinc-800 backdrop-blur-xl p-3 items-center gap-3 sticky top-0 z-10 ${zp.fullscreen ? "hidden" : "flex"}`}>
        <button data-testid="sketch-back-btn" onClick={() => (returnTab ? navigate(backUrl) : navigate(-1))} className="h-10 w-10 rounded-xl bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center"><ArrowLeft size={20} /></button>
        <input
          data-testid="sketch-title-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Skizzentitel…"
          className="flex-1 h-10 bg-transparent border-0 outline-none text-zinc-50 font-medium"
        />
        <select data-testid="sketch-category" value={category} onChange={(e) => setCategory(e.target.value)} className="h-10 bg-zinc-800 border border-zinc-700 rounded-xl px-3 text-sm">
          {SKETCH_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
        </select>
        <button
          data-testid="sketch-save-btn"
          onClick={save}
          disabled={saving}
          className="h-10 px-4 rounded-xl bg-[#00E575] text-[#09090B] font-semibold inline-flex items-center gap-1.5 hover:bg-[#00C964]"
        >
          {saving ? <Loader2 className="animate-spin" size={16} /> : <><Save size={16} /> Speichern</>}
        </button>
      </header>

      <div className={`flex-1 ${zp.fullscreen ? "p-0" : "p-3"}`}>
        <div className="relative w-full bg-[#18181B] rounded-2xl border border-zinc-800 overflow-hidden touch-none" style={{ height: zp.fullscreen ? "calc(100dvh - 76px)" : "calc(100vh - 180px)" }}
             onWheel={zp.onWheel} data-testid="sketch-stage">
          <ZoomControls zp={zp} testid="sketch-zoom" />
          <MeasureHint ruler={ruler} testid="sketch-ruler-hint" />
          <canvas
            ref={canvasRef}
            data-testid="sketch-canvas"
            className="sketch-canvas"
            style={{ ...zp.transformStyle, cursor: zp.panMode ? "grab" : "crosshair" }}
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          />
        </div>
      </div>

      <DimensionDialog ruler={ruler} />
      <WhatsNewTour page="draw" />
      <VCheckTransferBar ruler={ruler} projectId={id} />

      {/* Toolbar */}
      <div className="bg-[#09090B]/95 border-t border-zinc-800 backdrop-blur-xl p-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Palette size={16} className="text-zinc-400" />
            {COLORS.map((c) => (
              <button
                key={c}
                data-testid={`sketch-color-${c}`}
                onClick={() => { setColor(c); setIsEraser(false); }}
                className={`h-8 w-8 rounded-full border-2 transition ${color === c && !isEraser ? "border-white scale-110" : "border-zinc-700"}`}
                style={{ background: c }}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {SIZES.map((s) => (
              <button
                key={s}
                data-testid={`sketch-size-${s}`}
                onClick={() => setSize(s)}
                className={`h-9 w-9 rounded-full flex items-center justify-center transition ${size === s ? "bg-[#00E575]/15 ring-1 ring-[#00E575]" : "bg-zinc-800"}`}
              >
                <div className="rounded-full bg-zinc-100" style={{ width: s + 2, height: s + 2 }} />
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <RulerButton ruler={ruler} testid="sketch-ruler-btn" />
            <button
              data-testid="sketch-eraser-btn"
              onClick={() => { setIsEraser(!isEraser); ruler.setTool(null); }}
              className={`h-10 w-10 rounded-xl flex items-center justify-center ${isEraser ? "bg-[#00E575]/15 ring-1 ring-[#00E575]" : "bg-zinc-800"}`}
            >
              <Eraser size={18} />
            </button>
            <button
              data-testid="sketch-clear-btn"
              onClick={clear}
              className="h-10 w-10 rounded-xl bg-zinc-800 hover:bg-red-900/30 hover:text-red-400 flex items-center justify-center transition"
            >
              <Trash2 size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
